<template>
  <v-text-field v-if="!readOnly && !field.readOnly"
                :ref="inputRef"
                v-currency-input="numericMask"
                :value="fieldValue"
                :required="maybeCall(field.required, value)"
                :disabled="field.disabled"
                :hint="field.hint"
                :persistent-hint="true"
                :label="label"
                :class="field.class"
                :autofocus="field.autofocus"
                :rules="validationRules"
                :prepend-icon="field.prependIcon"
                :filled="box || field.box"
                :validate-on-blur="field.validateOnBlur"
                :prefix="field.prefix"
                :suffix="field.suffix"
                type="tel"
                persistent-placeholder
                @input="handleInput"
                @blur="handleChange" />
  <div v-else :class="{'reg-information': !small, 'reg-information-small': small}">
    <label>{{ field.label }}</label>
    <p v-if="field.hint" class="text-caption primary--text">
      {{ field.hint }}
    </p>
    <p>{{ fieldValue | formatNumber(completeNumericMask) }}</p>
  </div>
</template>

<script>
import { defineComponent, computed, watch, toRef } from 'vue'
import { useDynamicField } from '../composables/useDynamicField'
import { currencyInput } from '@/directives/currencyInput'

export default defineComponent({
  name: 'DynamicNumberField',
  directives: {
    currencyInput
  },
  props: {
    value: {
      type: Object,
      default: () => ({})
    },
    field: {
      type: Object,
      required: true
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    box: {
      type: Boolean,
      default: false
    },
    small: {
      type: Boolean,
      default: false
    },
    isValidating: {
      type: Boolean,
      default: false
    },
    itemKey: {
      type: String,
      default: ''
    }
  },
  emits: ['input', 'change'],
  setup(props, { emit, expose }) {
    const {
      fieldValue,
      validationRules,
      label,
      maybeCall,
      fieldChanged,
      inputRef,
      hasError
    } = useDynamicField({
      value: toRef(props, 'value'),
      field: props.field,
      readOnly: props.readOnly,
      isValidating: toRef(props, 'isValidating'),
      itemKey: props.itemKey,
      emptyValue: 0,
      emit
    })

    const numericMask = computed(() => ({
      decimal: props.field.decimal || ',',
      thousands: props.field.thousands || '.',
      prefix: '',
      suffix: '',
      precision: props.field.precision === undefined ? 2 : props.field.precision
    }))

    const completeNumericMask = computed(() => ({
      decimal: props.field.decimal || ',',
      thousands: props.field.thousands || '.',
      prefix: props.field.prefix || '',
      suffix: props.field.suffix || '',
      precision: props.field.precision === undefined ? 2 : props.field.precision,
      masked: false
    }))

    const handleInput = (value) => {
      fieldValue.value = value
    }

    const handleChange = () => {
      fieldChanged(fieldValue.value)
    }

    // Initialize fieldValue properly for string inputs
    watch(() => props.value[props.field.key], (value) => {
      if (typeof value === 'string' && value !== '') {
        fieldValue.value = parseFloat(value)
      }
    }, { immediate: true })

    // Expose properties for DynamicForm validation
    expose({
      hasError,
      validate: () => inputRef.value?.validate?.()
    })

    return {
      fieldValue,
      inputRef,
      validationRules,
      label,
      maybeCall,
      numericMask,
      completeNumericMask,
      handleInput,
      handleChange
    }
  }
})
</script>

<style lang="scss">
</style>
